import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type OrderModel from 'bcl/models/order';
import Wide from 'bcl/components/input/wide';
import SelectSuppliers from 'bcl/components/input/select-suppliers';
import SelectLocations from 'bcl/components/input/select-locations';
import { Input } from '@ember/component';
import Modal from 'bcl/components/modal';
import { fn } from '@ember/helper';
import HeroIcon from 'bcl/components/hero-icon';
import sort from 'bcl/helpers/sort';
import { on } from '@ember/modifier';
import eq from 'ember-truth-helpers/helpers/eq';
import type OrderEntryModel from 'bcl/models/order-entry';
import type SupplierModel from 'bcl/models/supplier';
import type SupplierItemModel from 'bcl/models/supplier-item';
import type LocationModel from 'bcl/models/location';
import amountName from 'bcl/helpers/amount-name';
import Store from '@ember-data/store';
import { service } from '@ember/service';
import WaitingSpinner from 'bcl/components/waiting-spinner';
import preventUnload from 'bcl/helpers/prevent-unload';
import type Owner from '@ember/owner';
import FeedbackWarning from 'bcl/components/feedback/warning';

interface OrderFormSignature {
  // The arguments accepted by the component
  Args: {
    save: () => void;
    cancel: () => void;
    model: OrderModel;
    location?: LocationModel;
    changeLocation?: (location: LocationModel) => void;
    isSaving: boolean;
  };
  // Any blocks yielded by the component
  Blocks: {
    default: [];
  };
  // The element to which `...attributes` is applied in the component template
  Element: null;
}

export default class OrderFormComponent extends Component<OrderFormSignature> {
  @service declare store: Store;

  @tracked showAlert = false;
  @tracked newSupplier?: SupplierModel;
  @tracked offendingItems = [];

  constructor(owner: Owner, args: OrderFormSignature['Args']) {
    super(owner, args);
    if (
      this.args.model.orderType === 'frei' ||
      this.args.model.orderType === 'special'
    ) {
      this.allShown = true;
    } else {
      this.allShown = false;
    }
  }

  @action changeSupplier(supplier: SupplierModel) {
    this.newSupplier = supplier;
    // find for every suplItem -> suplItem with new supplier
    type CurrentAndOld = {
      current: OrderEntryModel;
      newItem: SupplierItemModel;
    };
    const currentEntries = this.args.model.orderEntries ?? [];
    const newSuplItems: CurrentAndOld[] = currentEntries.map((entry) => ({
      current: entry,
      newItem: entry.supplier_item.item.supplierItems.filter(
        (suplI) => suplI.supplier === supplier
      )?.[0],
    }));

    if (
      !newSuplItems.every(
        (value) => value.newItem || value.current.amount === 0
      )
    ) {
      this.offendingItems = newSuplItems
        .map((value, idx) =>
          value.newItem || value.current.amount === 0
            ? null
            : currentEntries[idx]
        )
        .filter((value) => value);
      this.showAlert = true;
      return;
    }
    // change over to new supplier
    this.args.model.supplier = supplier;

    for (let i = newSuplItems.length - 1; i >= 0; i--) {
      if (newSuplItems[i].newItem) {
        this.args.model.orderEntries[i].supplier_item = newSuplItems[i].newItem;
      } else {
        // kann hier nur hinkommen, wenn amount 0 ist
        this.args.model.orderEntries[i].deleteRecord();
      }
    }
    if (
      this.args.model.orderType === 'frei' ||
      this.args.model.orderType === 'special'
    ) {
      this.toggleShowAll();
    } else {
      this.allShown = false;
    }
  }
  @tracked forceUpdate = 0;
  @action dontRemoveOffendingItems() {
    this.showAlert = false;
    this.forceUpdate++;
  }

  @action removeOffendingItems() {
    this.offendingItems.forEach((ordEntry) => {
      ordEntry.deleteRecord();
    });
    this.offendingItems = [];
    this.showAlert = false;
    this.changeSupplier(this.newSupplier);
  }

  @tracked allShown = false;
  @action toggleShowAll() {
    this.allShown = true;
    // add all which aren't in the current orderEntries list
    console.log(this.args.model.supplier?.name, this.args.model.supplier?.id);
    const allItems =
      this.args.model?.supplier?.supplierItems?.map((item) => item.id) ?? [];

    const alreadyAdded =
      this.args.model?.orderEntries?.map((entry) => entry.supplier_item.id) ??
      [];
    const toAdd = allItems.filter((x) => !alreadyAdded.includes(x));
    let position = alreadyAdded.length;
    for (let i = 0; i < toAdd.length; i++) {
      const suplItem = this.store.peekRecord('supplier-item', toAdd[i]);
      this.args.model?.orderEntries.push(
        this.store.createRecord('order-entry', {
          supplier_item: suplItem,
          amount: 0,
          position: position++,
        })
      );
    }
  }

  <template>
    {{preventUnload}}
    {{#if this.showAlert}}
      <Modal @size="big" @close={{fn (mut this.showAlert) false}}>

        <div class="sm:flex sm:items-start">
          <div
            class="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10"
          >
            <HeroIcon
              @icon="exclamation-triangle"
              class="text-red-600 size-6"
            />
          </div>
          <div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
            <h3
              class="text-base font-semibold leading-6 text-gray-900"
              id="modal-title"
            >Lieferant gewechselt</h3>
            <div class="mt-2">
              <p class="text-sm text-gray-500">Es konnten nicht für alle Artikel
                Zuordnungen zu dem neuen Lieferanten gefunden werden. Der
                Wechsel kann nur erfolgen, wenn die Einträge gelöscht werden</p>
            </div>
            <div class="text-gray-700">
              <h3 class="text-base font-semibold leading-6 mt-4">Folgende
                Einträge löschen</h3>
              {{#each
                (sort this.offendingItems "supplier_item.item.name")
                as |orderEntry|
              }}
                -
                {{orderEntry.supplier_item.item.name}}
                <br />
              {{/each}}
            </div>
          </div>
        </div>
        <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
          <button
            {{on "click" this.removeOffendingItems}}
            type="button"
            class="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
          >Einträge löschen und Lieferant wechseln</button>
          <button
            {{on "click" this.dontRemoveOffendingItems}}
            type="button"
            class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
          >nicht herauslöschen</button>
        </div>
      </Modal>
    {{/if}}
    <div class="block">
      <Wide
        @label="Datum"
        @name="date"
        @type="date"
        @value={{@model.html_delivery_date}}
      />
      <SelectSuppliers
        @label="Lieferant"
        @name="supplier"
        @onChange={{this.changeSupplier}}
        @value={{@model.supplier}}
        @forceUpdate={{this.forceUpdate}}
      />
      {{#if (eq @model.orderType "special")}}
        <SelectLocations
          @label="Lager"
          @name="location"
          @onChange={{@changeLocation}}
          @value={{@location}}
        />
        <Wide @label="Notiz" @name="note" @type="text" @value={{@model.note}} />
        <div class="rounded-md bg-blue-50 p-4 max-w-3xl mx-auto">
          <div class="flex">
            <div class="shrink-0">
              <svg
                class="size-5 text-blue-400"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
                data-slot="icon"
              >
                <path
                  fill-rule="evenodd"
                  d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-7-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM9 9a.75.75 0 0 0 0 1.5h.253a.25.25 0 0 1 .244.304l-.459 2.066A1.75 1.75 0 0 0 10.747 15H11a.75.75 0 0 0 0-1.5h-.253a.25.25 0 0 1-.244-.304l.459-2.066A1.75 1.75 0 0 0 9.253 9H9Z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div class="ml-3 flex-1 md:flex md:justify-between">
              <p class="text-sm text-blue-700">

                Eine positive Menge bedeutet, dass der Artikel zusätzlich
                gekauft wurde. Eine negative Zahl bedeutet, dass der Artikel aus
                dem Bestand entnommen wurde.
                <br />
                In der Notiz kann festgehalten werden, wo der Einkauf
                stattgefunden hat oder warum Artikel entnommen wurden.
                <br />
                Dadurch entnommene Artikel werden nicht in die Berechnung des
                Verbrauchs einbezogen.</p>

            </div>
          </div>
        </div>
        <div class="mt-1 mx-10 text-sm leading-6 text-gray-600">

        </div>
      {{/if}}
    </div>

    <div class="mt-8 flow-root">
      <div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div class="inline-block min-w-full py-2 align-middle px-4 lg:px-8">
          <table class="min-w-full divide-y divide-gray-300">
            <thead>
              <tr>
                <th
                  scope="col"
                  class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-3"
                >Name</th>
                <th
                  scope="col"
                  class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-3"
                >Artikel-Nummer</th>
                <th
                  scope="col"
                  class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                >{{#if (eq @model.orderType "special")}}
                    Menge
                  {{else}}
                    Bestellmenge
                  {{/if}}</th>
              </tr>
            </thead>
            <tbody class="bg-white">
              {{#each
                (sort @model.orderEntries "supplier_item.item.name")
                as |orderEntry|
              }}
                <tr class="even:bg-gray-50">
                  <td
                    class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-3"
                  >
                    {{orderEntry.supplier_item.item.name}}
                    ({{orderEntry.supplier_item.item.gebinde}})</td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {{orderEntry.supplier_item.articlenr}}
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    <Input
                      @type="text"
                      name="bundleAmount"
                      id="bundleAmount"
                      @value={{orderEntry.amount}}
                      class="inline w-16 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:max-w-xs sm:text-sm sm:leading-6"
                    />
                    <span class="ml-2 text-sm text-gray-500">
                      {{amountName orderEntry.supplier_item.item 2}}
                    </span>
                  </td>

                </tr>
              {{else}}
                <tr class="even:bg-gray-50">
                  <td
                    colspan="3"
                    class="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-3"
                  >
                    <p class="text-gray-500">
                      {{#if this.allShown}}
                        Keine Artikel, die für diesen Lieferanten Einträge
                        haben.
                      {{else}}
                        Keine Artikel in dieser Bestellung vorhanden.
                      {{/if}}
                    </p>
                  </td>
                </tr>
              {{/each}}

            </tbody>
          </table>
        </div>
      </div>
    </div>
    <div class="block mt-4">
      {{#unless this.allShown}}
        <button
          {{on "click" this.toggleShowAll}}
          type="button"
          class="rounded-md bg-indigo-600 px-3 mt-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        >
          Alle Artikel anzeigen
        </button>
        <p class="mt-1 text-sm leading-6 text-gray-600">Es werden nur Artikel
          hinzugefügt, die einen Eintrag für diesen Lieferanten haben.</p>
      {{/unless}}
    </div>
    <div class="mt-6 flex items-center justify-end gap-x-6">
      <FeedbackWarning @header="Lieferdatum anpassen">
        Lieferdatum auswählen nicht vergessen :)
      </FeedbackWarning>
    </div>
    <div class="mt-6 flex items-center justify-end gap-x-6">
      <button
        {{on "click" @cancel}}
        type="button"
        class="text-sm font-semibold leading-6 text-gray-900"
      >abbrechen</button>
      <button
        {{on "click" @save}}
        type="submit"
        class="rounded-md
          {{if
            @isSaving
            'bg-gray-600  hover:bg-gray-500 focus-visible:outline-gray-600'
            'bg-indigo-600 hover:bg-indigo-500 focus-visible:outline-indigo-600'
          }}
          px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline-2 focus-visible:outline-offset-2"
      >
        Speichern
        {{#if @isSaving}}
          <WaitingSpinner class="inline ml-2" />
        {{/if}}
      </button>
    </div>
  </template>
}
